import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff, Loader2, CheckCircle, XCircle, KeyRound } from 'lucide-react';
import Logo from '../components/Logo';

export default function ResetPasswordPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [valid, setValid] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/auth/validate-reset-token/${token}`)
      .then(r => { setValid(r.data.valid); setEmail(r.data.email || ''); })
      .catch(() => setValid(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (password !== confirm) { setError('Passwords do not match'); return; }
    setLoading(true); setError('');
    try {
      await axios.post(`${API}/auth/reset-password/${token}`, { password });
      setDone(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to reset password');
    } finally { setLoading(false); }
  };

  const inputStyle = { background: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.12)', color: 'white' };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#0A0F1E' }}>
      <div className="w-full max-w-sm">
        <div className="flex justify-center mb-8"><Logo size={36} showText light /></div>
        {valid === null && <div className="text-center"><Loader2 size={28} className="text-blue-400 animate-spin mx-auto" /></div>}
        {valid === false && (
          <div className="text-center">
            <XCircle size={48} className="text-red-400 mx-auto mb-4" />
            <h2 className="font-heading text-xl font-bold text-white mb-2">Link expired or invalid</h2>
            <p className="text-sm mb-6" style={{ color: 'rgba(148,163,184,0.7)' }}>This reset link has expired or was already used.</p>
            <Link to="/forgot-password"><Button className="w-full h-11">Request new link</Button></Link>
          </div>
        )}
        {valid === true && !done && (
          <>
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5"
              style={{ background: 'rgba(37,99,235,0.15)', border: '1px solid rgba(37,99,235,0.3)' }}>
              <KeyRound size={22} className="text-blue-400" />
            </div>
            <h2 className="font-heading text-2xl font-bold text-white mb-1">Set new password</h2>
            <p className="text-sm mb-7" style={{ color: 'rgba(148,163,184,0.7)' }}>For <strong className="text-white">{email}</strong></p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-400">New Password</Label>
                <div className="relative">
                  <Input type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                    placeholder="Min 6 characters" style={inputStyle} className="h-11 pr-10" autoFocus />
                  <button type="button" onClick={() => setShowPw(!showPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: 'rgba(148,163,184,0.4)' }}>
                    {showPw ? <EyeOff size={15}/> : <Eye size={15}/>}
                  </button>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-400">Confirm Password</Label>
                <Input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Repeat password" style={inputStyle} className="h-11" />
              </div>
              {error && <p className="text-xs text-red-400">{error}</p>}
              <Button type="submit" className="w-full h-11 gap-2 font-semibold" disabled={loading}>
                {loading ? <><Loader2 size={15} className="animate-spin"/> Resetting…</> : 'Set New Password'}
              </Button>
            </form>
          </>
        )}
        {done && (
          <div className="text-center">
            <CheckCircle size={48} className="text-green-500 mx-auto mb-4" />
            <h2 className="font-heading text-xl font-bold text-white mb-2">Password updated!</h2>
            <p className="text-sm" style={{ color: 'rgba(148,163,184,0.7)' }}>Redirecting to sign in…</p>
          </div>
        )}
      </div>
    </div>
  );
}
